import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getLeaderboard, getLeaderboardHistory } from '../api';
import PageHeader from '../components/PageHeader';
import StandingsHistoryChart from '../components/StandingsHistoryChart';
import PushButton from '../components/PushButton';
import LiveBanner from '../components/LiveBanner';
import MatchdayBrowser from '../components/MatchdayBrowser';
import StreaksAndToday from '../components/StreaksAndToday';
import type { PlayerStanding } from '../types';
import { TZ } from '../format';

type SortMode = 'actual' | 'projection' | 'ai';
const SORT_KEY = 'wcfantasy.standings-sort';

function valueFor(s: PlayerStanding, mode: SortMode): number {
  if (mode === 'actual') return s.points;
  if (mode === 'projection') return s.projectedPoints;
  return s.aiProjectedPoints;
}

export default function Leaderboard() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: getLeaderboard,
  });
  const { data: history } = useQuery({
    queryKey: ['leaderboard-history'],
    queryFn: getLeaderboardHistory,
  });
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    try {
      const v = localStorage.getItem(SORT_KEY);
      return v === 'projection' || v === 'ai' ? v : 'actual';
    } catch { return 'actual'; }
  });
  function pickSort(m: SortMode) {
    setSortMode(m);
    try { localStorage.setItem(SORT_KEY, m); } catch { /* ignore */ }
  }

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data.standings].sort((a, b) => {
      const va = valueFor(a, sortMode);
      const vb = valueFor(b, sortMode);
      if (vb !== va) return vb - va;
      // Tie-break: actual points, then projected (so order is stable / sensible).
      if (b.points !== a.points) return b.points - a.points;
      return b.projectedPoints - a.projectedPoints;
    });
  }, [data, sortMode]);

  if (isLoading) return <Skeleton />;
  if (error) return <ErrorBox msg={(error as Error).message} />;
  if (!data) return null;

  const leaderValue = sorted[0] ? valueFor(sorted[0], sortMode) : 0;
  const maxProjected = Math.max(...data.standings.map((s) => Math.max(s.projectedPoints, s.aiProjectedPoints)), 1);

  return (
    <>
      <PageHeader
        title="Standings"
        subtitle={
          <>
            All times {TZ}. Updated {new Date(dataUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: TZ })}.
            {data.lastPoll && (
              <> Data {new Date(data.lastPoll).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: TZ })}.</>
            )}
          </>
        }
      />

      <LiveBanner />
      <div className="px-4 lg:px-0">
        <MatchdayBrowser />
      </div>

      <div className="px-4 lg:px-0 mb-3">
        <PushButton />
      </div>
      <div className="px-4 lg:px-0 mb-4">
        <StandingsHistoryChart data={history} />
      </div>

      <div className="px-4 lg:px-0 mb-4">
        <StreaksAndToday />
      </div>

      <div className="px-4 lg:px-0">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="text-xs uppercase tracking-widest text-dim">Ranked by</h2>
          <div className="flex rounded-md bg-ink-800 p-0.5 text-[10px]">
            <SortBtn label="Actual" active={sortMode === 'actual'} onClick={() => pickSort('actual')} tone="now" />
            <SortBtn label="Projection" active={sortMode === 'projection'} onClick={() => pickSort('projection')} tone="proj" />
            <SortBtn label="AI" active={sortMode === 'ai'} onClick={() => pickSort('ai')} tone="ai" />
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-0 space-y-3">
        {sorted.map((s, i) => (
          <Row key={s.name} standing={s} rank={i + 1} leader={leaderValue} maxProjected={maxProjected} sortMode={sortMode} />
        ))}
        <p className="text-[11px] text-dim pt-3 max-w-prose mx-auto text-center">
          <strong>Projected</strong> = current pts + bookmaker-implied expected pts from upcoming matches.
        </p>
      </div>
    </>
  );
}

function Row({ standing, rank, leader, maxProjected, sortMode }: {
  standing: PlayerStanding; rank: number; leader: number; maxProjected: number; sortMode: SortMode;
}) {
  const myValue = valueFor(standing, sortMode);
  const gap = leader - myValue;
  const isLeader = rank === 1;
  const gapLabel = sortMode === 'actual' ? 'pts' : sortMode === 'projection' ? 'proj' : 'ai';

  return (
    <Link
      to={`/players/${encodeURIComponent(standing.name)}`}
      className={`card block p-4 hover:border-pitch-500/40 transition-colors ${isLeader ? 'ring-1 ring-pitch-500/40' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full grid place-items-center font-mono text-sm ${
          isLeader ? 'bg-pitch-500/15 text-pitch-400 ring-1 ring-pitch-500/30' : 'bg-ink-700 text-dim'
        }`}>
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-chalk truncate">{standing.name}</div>
          <div className="text-xs text-dim">{gap < 0.05 ? 'Leader' : `−${gap.toFixed(sortMode === 'actual' ? 0 : 1)} ${gapLabel}`}</div>
        </div>
        <div className="text-right grid grid-cols-3 gap-x-3 items-end">
          <Stat value={standing.points.toString()} label="now" active={sortMode === 'actual'} tone="chalk" />
          <Stat value={standing.projectedPoints.toFixed(1)} label="proj" active={sortMode === 'projection'} tone="pitch" />
          <Stat value={standing.aiProjectedPoints.toFixed(1)} label="ai" active={sortMode === 'ai'} tone="cyan" />
        </div>
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-ink-800 overflow-hidden relative">
        <div className="absolute inset-y-0 left-0 bg-cyan-500/30" style={{ width: `${(standing.aiProjectedPoints / maxProjected) * 100}%` }} />
        <div className="absolute inset-y-0 left-0 bg-pitch-500/40" style={{ width: `${(standing.projectedPoints / maxProjected) * 100}%` }} />
        <div className="absolute inset-y-0 left-0 bg-pitch-400" style={{ width: `${(standing.points / maxProjected) * 100}%` }} />
      </div>

      <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        {standing.teams.map((t) => (
          <div key={t.code} className="flex items-center gap-1.5 rounded-lg bg-ink-800/60 px-2 py-1 min-w-0">
            <span className="text-base leading-none">{t.flag}</span>
            <span className="text-xs truncate flex-1">{t.code}</span>
            <span className="stat text-xs text-pitch-400">
              {t.points}
              {t.expectedRemaining > 0 && <span className="text-dim">+{t.expectedRemaining.toFixed(1)}</span>}
            </span>
          </div>
        ))}
      </div>
    </Link>
  );
}

function SortBtn({ label, active, onClick, tone }: { label: string; active: boolean; onClick: () => void; tone: 'now' | 'proj' | 'ai' }) {
  const activeColor = tone === 'proj' ? 'text-pitch-400' : tone === 'ai' ? 'text-cyan-400' : 'text-chalk';
  return (
    <button
      type="button" onClick={onClick}
      className={`px-2 py-1 rounded transition-colors ${active ? `bg-ink-700 ${activeColor}` : 'text-dim'}`}
    >{label}</button>
  );
}

function Stat({ value, label, active, tone }: { value: string; label: string; active: boolean; tone: 'chalk' | 'pitch' | 'cyan' }) {
  const color = tone === 'pitch' ? 'text-pitch-400' : tone === 'cyan' ? 'text-cyan-400' : 'text-chalk';
  return (
    <div className={active ? '' : 'opacity-50'}>
      <div className={`stat text-xl sm:text-2xl font-semibold ${color}`}>{value}</div>
      <div className={`text-[10px] uppercase tracking-wider ${active ? color : 'text-dim'}`}>{label}</div>
    </div>
  );
}

function Skeleton() {
  return (
    <>
      <PageHeader title="Standings" />
      <div className="px-4 lg:px-0 space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="card p-4 h-24 animate-pulse bg-ink-900/60" />
        ))}
      </div>
    </>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="px-4 lg:px-0">
      <div className="card p-6 text-sm text-red-300 border-red-900/40">Failed to load: {msg}</div>
    </div>
  );
}
